/**
 * The ticking elapsed counter beside a working card's status label.
 *
 * ┌────────────────────────────────────┐
 * │ ▣ forge              ◌ Working 42s │
 * └────────────────────────────────────┘
 *
 * Ported from t3code's `WorkingDuration`. Without it the "Working" label is a
 * static word: it tells you a thread is busy but not whether it has been busy
 * for four seconds or forty minutes, which is the difference between "wait" and
 * "go look at it". That distinction is the whole reason the card shows a status
 * instead of a spinner.
 *
 * **Why this is its own component and not a value computed in the card.**
 * It re-renders once a second. Rendered inline, every Activity card with a
 * running thread would re-render its entire three-line body every second —
 * project line, title, meta line, tooltip subtree and all. As a leaf, the
 * interval's `setState` re-renders exactly this `<span>`. t3code makes the same
 * split for the same reason, and its comment says so outright.
 *
 * The interval is a genuine subscription to wall-clock time, so it is one of
 * the few places a `useEffect` is correct rather than a derived-state smell:
 * there is no prop or store value that changes when a second passes.
 */

import { useEffect, useState } from "react";

import { formatActivityCardWorkingDuration } from "./activityThreadCard.logic";

export interface ActivityCardWorkingDurationProps {
  /** ISO instant the current turn started, or `null` when unknown. */
  readonly startedAt: string | null;
}

export function ActivityCardWorkingDuration({ startedAt }: ActivityCardWorkingDurationProps) {
  const startedMs = startedAt !== null ? Date.parse(startedAt) : Number.NaN;
  // The tick is the point; the value is read from the clock during render.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (Number.isNaN(startedMs)) return;
    const id = window.setInterval(() => setTick((tick) => tick + 1), 1_000);
    return () => window.clearInterval(id);
  }, [startedMs]);
  // A malformed or absent timestamp renders nothing rather than "0s": a counter
  // frozen at zero reads as a stalled agent, which is a worse lie than silence.
  if (Number.isNaN(startedMs)) return null;
  return (
    <span className="font-mono tabular-nums">
      {formatActivityCardWorkingDuration(Date.now() - startedMs)}
    </span>
  );
}
